import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";
import type { CredentialStatus } from "./classify";

const STATUS_LABEL: Record<CredentialStatus, string> = {
  expired: "Expired",
  missing: "Missing",
  "expiring-soon": "Expiring soon",
  current: "Current",
};

export function CredentialStatusBadge({
  status,
  optional = false,
  className,
}: {
  status: CredentialStatus;
  optional?: boolean;
  className?: string;
}) {
  if (status === "missing" && optional) {
    return (
      <Badge
        variant="outline"
        className={cn("gap-1 whitespace-nowrap", className)}
      >
        <HelpCircle className="h-3.5 w-3.5 shrink-0" />
        Optional
      </Badge>
    );
  }

  if (status === "expired") {
    return (
      <Badge
        variant="destructive"
        className={cn("gap-1 whitespace-nowrap", className)}
      >
        <AlertOctagon className="h-3.5 w-3.5 shrink-0" />
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === "missing") {
    return (
      <Badge
        variant="destructive"
        className={cn("gap-1 whitespace-nowrap", className)}
      >
        <HelpCircle className="h-3.5 w-3.5 shrink-0" />
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === "expiring-soon") {
    return (
      <Badge
        variant="warning"
        className={cn("gap-1 whitespace-nowrap", className)}
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 whitespace-nowrap border-emerald-300 text-emerald-700",
        className,
      )}
    >
      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export function severityDotClass(status: CredentialStatus): string {
  if (status === "expired" || status === "missing") {
    return "bg-red-500";
  }
  if (status === "expiring-soon") {
    return "bg-amber-500";
  }
  return "bg-emerald-500";
}

export function documentSeverityDotClass({
  status,
  optional,
}: {
  status: CredentialStatus;
  optional: boolean;
}): string {
  if (status === "missing" && optional) {
    return "bg-muted-foreground/40";
  }
  return severityDotClass(status);
}
